import { forwardRef, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, Download, ZoomIn } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { HappyHour } from "./HappyHour";
import { Seo } from "./Seo";
import { useContent } from "./ContentContext";
import { useTx, txtStyle } from "./siteText";
import { useLightbox } from "./Lightbox";

const Page = forwardRef(({ src, num, onZoom }, ref) => (
  <div ref={ref} className="bg-cantina-cocoadeep overflow-hidden" data-testid={`drinks-page-${num}`}>
    <img
      src={src}
      alt={`Bar menu page ${num}`}
      className="w-full h-full object-cover select-none cursor-zoom-in"
      draggable={false}
      loading={num <= 2 ? "eager" : "lazy"}
      onClick={onZoom}
    />
  </div>
));
Page.displayName = "DrinksPage";

const RATIO = 1087 / 2426; // page width / height (tall menu page)

export default function Drinks() {
  const lenis = useLenis();
  const { settings, resolveImg } = useContent();
  const tx = useTx();
  const { show } = useLightbox();
  const bookRef = useRef(null);
  const [page, setPage] = useState(0);
  const [dims, setDims] = useState({ w: 340, h: 760, mobile: false });

  const pages = settings.menus?.drinksPages || [];
  const pdf = settings.menus?.drinksPdf;
  const total = pages.length;
  const lightItems = pages.map((p, i) => ({ img: p, name: `Bar Menu — Page ${i + 1}` }));

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [lenis]);

  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      const mobile = vw < 768;
      if (mobile) {
        const w = Math.min(vw - 48, 380);
        setDims({ w, h: Math.round(w / RATIO), mobile: true });
      } else {
        // fit two pages side-by-side within viewport height and width
        const h = Math.max(520, Math.min(window.innerHeight - 300, 780));
        let w = Math.round(h * RATIO);
        const maxSpread = Math.min(vw - 180, 980); // room for side arrows
        if (w * 2 > maxSpread) { w = Math.floor(maxSpread / 2); }
        setDims({ w, h: Math.round(w / RATIO), mobile: false });
      }
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const flip = (dir) => {
    const api = bookRef.current?.pageFlip?.();
    if (!api) return;
    if (dir === "next") api.flipNext();
    else api.flipPrev();
  };

  return (
    <div className="relative bg-cantina-cocoa min-h-screen" data-testid="drinks-page">
      <Seo page="drinks" path="/drinks" />
      <Navbar />

      <header className="relative pt-[100px] bg-cantina-cocoadeep text-cantina-cream overflow-hidden">
        <div className="absolute inset-0 tile-watermark opacity-[0.06]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-16 text-center">
          <p className="font-script text-cantina-marigold mb-1" style={txtStyle(tx("drinks.eyebrow"))}>{tx("drinks.eyebrow").text}</p>
          <h1 className="font-display tracking-tight" style={txtStyle(tx("drinks.headline"))}>{tx("drinks.headline").text}</h1>
          <p className="mt-4 font-sans text-cantina-cream/70 max-w-xl mx-auto" style={txtStyle(tx("drinks.subtitle"))}>{tx("drinks.subtitle").text}</p>
        </div>
      </header>

      <section className="relative py-12 md:py-16 bg-cantina-cocoa" data-testid="drinks-flipbook-section">
        {total === 0 ? (
          <p className="text-center text-cantina-cream/60 font-sans py-20">The bar menu is being updated. Please check back soon.</p>
        ) : (
          <div className="relative max-w-[1000px] mx-auto px-4 md:px-10 flex flex-col items-center">
            <div className="relative w-full flex items-center justify-center">
              {/* side arrows (desktop) */}
              <button onClick={() => flip("prev")} aria-label="Previous page" data-testid="drinks-prev"
                className="hidden md:flex absolute left-0 z-20 w-12 h-12 rounded-full bg-cantina-marigold text-cantina-cocoa items-center justify-center shadow-lg hover:scale-105 transition-transform disabled:opacity-30"
                disabled={page === 0}>
                <ChevronLeft size={24} />
              </button>

              <div className="drinks-book-wrap" data-testid="drinks-flipbook" style={{ perspective: "2200px" }}>
                <HTMLFlipBook
                  key={dims.mobile ? "book-m" : "book-d"}
                  ref={bookRef}
                  width={dims.w}
                  height={dims.h}
                  size="fixed"
                  drawShadow
                  maxShadowOpacity={0.6}
                  showCover
                  usePortrait={dims.mobile}
                  flippingTime={850}
                  mobileScrollSupport
                  useMouseEvents
                  onFlip={(e) => setPage(e.data)}
                  className="drinks-book mx-auto"
                  style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}
                >
                  {pages.map((p, i) => (
                    <Page key={i} src={resolveImg(p)} num={i + 1} onZoom={() => show(lightItems, i)} />
                  ))}
                </HTMLFlipBook>
              </div>

              <button onClick={() => flip("next")} aria-label="Next page" data-testid="drinks-next"
                className="hidden md:flex absolute right-0 z-20 w-12 h-12 rounded-full bg-cantina-marigold text-cantina-cocoa items-center justify-center shadow-lg hover:scale-105 transition-transform disabled:opacity-30"
                disabled={page >= total - 1}>
                <ChevronRight size={24} />
              </button>
            </div>

            {/* mobile controls + counter + hint */}
            <div className="mt-8 flex items-center gap-4">
              <button onClick={() => flip("prev")} aria-label="Previous page" data-testid="drinks-prev-mobile"
                className="md:hidden w-11 h-11 rounded-full bg-cantina-marigold text-cantina-cocoa flex items-center justify-center shadow disabled:opacity-30" disabled={page === 0}>
                <ChevronLeft size={22} />
              </button>
              <span className="font-mono text-xs tracking-[0.3em] text-cantina-cream/60" data-testid="drinks-counter">
                {Math.min(page + 1, total)} / {total}
              </span>
              <button onClick={() => flip("next")} aria-label="Next page" data-testid="drinks-next-mobile"
                className="md:hidden w-11 h-11 rounded-full bg-cantina-marigold text-cantina-cocoa flex items-center justify-center shadow disabled:opacity-30" disabled={page >= total - 1}>
                <ChevronRight size={22} />
              </button>
            </div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="mt-3 flex items-center gap-2 font-sans text-cantina-cream/40" style={txtStyle(tx("drinks.hint"))}>
              <ZoomIn size={13} /> {tx("drinks.hint").text} · tap a page to zoom
            </motion.p>

            {pdf && (
              <a href={resolveImg(pdf)} target="_blank" rel="noopener noreferrer" download
                className="btn-primary mt-8 inline-flex items-center gap-2 bg-cantina-marigold text-cantina-cocoa font-sans tracking-wide px-8 py-4 rounded-full"
                style={{ boxShadow: "0 12px 30px rgba(242,182,50,0.35)", ...txtStyle(tx("drinks.downloadLabel")) }}
                data-testid="drinks-download">
                <Download size={16} /> {tx("drinks.downloadLabel").text}
              </a>
            )}
          </div>
        )}
      </section>

      <HappyHour variant="drinks" />

      <Footer />
    </div>
  );
}
